import { BusinessCreateResponseType } from "@sellar-io/api-v2/types";
import { Spinner, SpinnerContainer } from "@sellar-io/components";
import { useForm } from "@tanstack/react-form";
import { useSearchParams } from "react-router";
import { useEvents } from "~/hooks";
import { useBusinessCreate } from "~/hooks/api/v2/business/useBusinessCreate";
import { BusinessForm } from "./BusinessForm";
import {
  generateBusinessFormFieldValues,
  generateBusinessRequest,
  generateSellarAddressFromGoogleResults,
  type BusinessFormFieldValues,
  type BusinessFormType,
} from "./BusinessFormValues";

type BusinessFormCreateCommonProps = {
  metadata?: {
    createdFrom: "Business switcher" | "Add customer";
    initiator: "supplier" | "retailer";
    source?: "new-venue";
  };
  searchGooglePlacesResult?: google.maps.places.PlaceResult;
  lockEmail?: boolean;
  formId: string;
};

type BusinessFormCreateBusinessProps = BusinessFormCreateCommonProps & {
  mode: "business";
  type: BusinessFormType;
  onCreate: (business: BusinessCreateResponseType & { email?: string }) => void;
};

type BusinessFormCreateSupplierCustomerProps = BusinessFormCreateCommonProps & {
  mode: "supplier-customer";
  type: "buyer";
  onCreate: (result: {
    business: BusinessCreateResponseType & { email: string };
    deliveryInstructions?: string;
  }) => void;
};

export type BusinessFormCreateProps =
  | BusinessFormCreateBusinessProps
  | BusinessFormCreateSupplierCustomerProps;

export const BusinessFormCreate = (props: BusinessFormCreateProps) => {
  const {
    type,
    metadata,
    searchGooglePlacesResult,
    lockEmail = false,
    formId,
  } = props;
  const { track } = useEvents();
  const [searchParams] = useSearchParams();
  const businessCreateMutation = useBusinessCreate();

  const form = useForm({
    defaultValues: searchGooglePlacesResult
      ? ({
          ...generateBusinessFormFieldValues(),
          name: searchGooglePlacesResult.name ?? "",
          addresses: {
            DeliveryAddress: generateSellarAddressFromGoogleResults(
              searchGooglePlacesResult,
            ),
            InvoiceAddress: generateSellarAddressFromGoogleResults(
              searchGooglePlacesResult,
            ),
            CollectionAddress: generateSellarAddressFromGoogleResults(
              searchGooglePlacesResult,
            ),
            isInvoiceAddressSame: true,
            isCollectionAddressSame: true,
          },
        } satisfies BusinessFormFieldValues)
      : generateBusinessFormFieldValues(),
    onSubmit: ({ value }) => {
      if (type === "supplier") {
        const request = generateBusinessRequest(value, "supplier");
        // creating a supplier, no mixpanel data collected, hence no metadata provided
        return businessCreateMutation.mutate(
          {
            data: request,
          },
          {
            onSuccess: (business) => {
              track("Supplier Created");
              props.onCreate(business);
            },
          },
        );
      }

      const request = generateBusinessRequest(value, "buyer");

      if (type === "buyer") {
        const connectionDeliveryInstructions =
          props.mode === "supplier-customer"
            ? (request.deliveryInstructions ?? undefined)
            : undefined;
        const businessRequest =
          props.mode === "supplier-customer"
            ? { ...request, deliveryInstructions: null }
            : request;
        const storefrontLinkPassword =
          searchParams.get("storefrontLink") ??
          searchParams.get("priceTierLink");
        const businessUserInviteJwt = searchParams.get("jwt");
        const utmMedium = searchParams.get("utm_medium");

        return businessCreateMutation.mutate(
          {
            data: businessRequest,
            ...(!!metadata && {
              sellarMetadata: {
                storefrontLinkPassword,
                businessUserInviteJwt,
                utmMedium,
                initiator: metadata.initiator,
                createdFrom: metadata.createdFrom,
              },
            }),
          },
          {
            onSuccess: (business) => {
              const createdBusiness = { ...business, email: request.email };

              if (props.mode === "supplier-customer") {
                props.onCreate({
                  business: createdBusiness,
                  deliveryInstructions: connectionDeliveryInstructions,
                });
                return;
              }

              props.onCreate(createdBusiness);
            },
          },
        );
      }

      throw new Error("Unknown business type");
    },
  });

  if (businessCreateMutation.isPending)
    return (
      <SpinnerContainer>
        <Spinner />
      </SpinnerContainer>
    );

  return (
    <BusinessForm
      type={type}
      form={form}
      lockEmail={lockEmail}
      metadata={metadata}
      formId={formId}
    />
  );
};
